import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import {
  smaltCoreIngestSyncApi,
  SmaltCoreIngestCheckResponse,
  SmaltCoreIngestSyncResponse,
} from '@/api/smaltCoreIngestSyncApi';

export function SmaltCoreIngestSyncPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();

  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<SmaltCoreIngestSyncResponse | null>(null);
  const [checkError, setCheckError] = useState<string>('');
  const [checkResult, setCheckResult] = useState<SmaltCoreIngestCheckResponse | null>(null);
  const [orphanResult, setOrphanResult] = useState<{
    attributeCount: number;
    deviceCount: number;
    deviceNames: string[];
  } | null>(null);
  const [orphanError, setOrphanError] = useState<string>('');

  const platformProjectId = useMemo(() => {
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri]);

  useEffect(() => {
    setOrphanResult(null);
    setOrphanError('');
  }, [platformProjectId]);

  const terminalLines = useMemo(() => {
    if (!checkResult) return [];

    const header = [
      `Check smalt_core (project=${checkResult.coreProject.code})`,
      `summary: total=${checkResult.summary.total} ok=${checkResult.summary.ok} missing=${checkResult.summary.missing} mismatched=${checkResult.summary.mismatched} invalid=${checkResult.summary.invalid}`,
      '---',
    ];

    const lines = checkResult.problems.map((p) => {
      const id = `attr_id=${p.platformAttributeId}`;
      const uuid = p.uuid ? ` uuid=${p.uuid}` : '';
      const name = p.name ? ` name="${p.name}"` : '';

      let extra = '';
      if (p.type === 'MISMATCH' && p.diffs) {
        const fields = Object.keys(p.diffs).sort();
        extra = ` diffs=[${fields.join(', ')}]`;
      }

      return `${id}${uuid}${name} -> ${p.type}: ${p.message}${extra}`;
    });

    return [...header, ...lines];
  }, [checkResult]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!platformProjectId) throw new Error('Please select a project');
      return smaltCoreIngestSyncApi.sync(platformProjectId);
    },
    onMutate: () => {
      setError('');
      setResult(null);
      setCheckError('');
      setCheckResult(null);
    },
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (e: any) => {
      setError(e?.body?.message || e?.message || 'Sync failed');
    },
  });

  const checkMutation = useMutation({
    mutationFn: async () => {
      if (!platformProjectId) throw new Error('Please select a project');
      return smaltCoreIngestSyncApi.check(platformProjectId);
    },
    onMutate: () => {
      setCheckError('');
      setCheckResult(null);
      setError('');
      setResult(null);
    },
    onSuccess: (data) => {
      setCheckResult(data);
    },
    onError: (e: any) => {
      setCheckError(e?.body?.message || e?.message || 'Check failed');
    },
  });

  const orphanMutation = useMutation({
    mutationFn: async () => {
      if (!platformProjectId) throw new Error('Veuillez sélectionner un projet');
      return smaltCoreIngestSyncApi.orphanDevices(platformProjectId);
    },
    onMutate: () => {
      setOrphanError('');
      setOrphanResult(null);
    },
    onSuccess: (data) => {
      setOrphanResult({
        attributeCount: data.attributeCount ?? 0,
        deviceCount: data.deviceCount ?? 0,
        deviceNames: data.deviceNames ?? [],
      });
    },
    onError: (e: unknown) => {
      const body = (e as { body?: { message?: string } })?.body;
      setOrphanError(body?.message || (e instanceof Error ? e.message : 'Erreur'));
    },
  });

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Administration</span> / Smalt Core Ingest Sync
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Smalt Core Ingest Sync</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Platform Project</label>
                <ProjectSearchSelect value={selectedProjectIri} onChange={setSelectedProjectIri} />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={!platformProjectId || syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">sync</span>
                  Synchroniser
                </button>

                <button
                  type="button"
                  disabled={!platformProjectId || checkMutation.isPending}
                  onClick={() => checkMutation.mutate()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#16202A] text-slate-800 dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">fact_check</span>
                  Vérifier
                </button>

                <button
                  type="button"
                  disabled={!platformProjectId || orphanMutation.isPending}
                  onClick={() => orphanMutation.mutate()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">devices_other</span>
                  Attributes orphelins (room/apartment null)
                </button>

                {syncMutation.isPending && (
                  <span className="text-sm text-slate-500 dark:text-slate-400">Synchronizing...</span>
                )}
                {checkMutation.isPending && (
                  <span className="text-sm text-slate-500 dark:text-slate-400">Vérification...</span>
                )}
                {orphanMutation.isPending && (
                  <span className="text-sm text-slate-500 dark:text-slate-400">Chargement...</span>
                )}
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {checkError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {checkError}
                </div>
              )}

              {orphanError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {orphanError}
                </div>
              )}

              {result && (
                <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116] px-4 py-3 text-sm space-y-2">
                  <div className="font-medium text-slate-900 dark:text-white">Result</div>
                  <div>
                    <div>
                      <span className="text-slate-500 dark:text-slate-400">Core project</span>: {result.coreProject.code}{' '}
                      (id={result.coreProject.id}) — created={String(result.coreProject.created)}
                    </div>
                    {result.coreProject.created && result.coreProject.ingest_key && (
                      <div className="mt-1">
                        <span className="text-slate-500 dark:text-slate-400">ingest_key</span>:&nbsp;
                        <code className="px-1 py-0.5 rounded bg-slate-200/60 dark:bg-slate-800">
                          {result.coreProject.ingest_key}
                        </code>
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Attributes</span>: created={result.attributes.created},{' '}
                    updated={result.attributes.updated}, skipped={result.attributes.skipped}, invalid={result.attributes.invalid}
                  </div>
                </div>
              )}

              {orphanResult !== null && (
                <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116] overflow-hidden">
                  <div className="px-4 py-3 font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-[#1E293B]">
                    Devices (attributes smalt_core avec room_id ou apartment_id null)
                  </div>
                  <div className="px-4 py-3 flex flex-wrap gap-6 border-b border-slate-200 dark:border-[#1E293B] bg-slate-100/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Attributes concernés:</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{orphanResult.attributeCount}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Devices concernés:</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{orphanResult.deviceCount}</span>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {orphanResult.deviceNames.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400 text-center">
                        Aucun device trouvé
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-800/50 sticky top-0">
                          <tr>
                            <th className="text-left px-4 py-2.5 font-medium text-slate-600 dark:text-slate-300 w-12">#</th>
                            <th className="text-left px-4 py-2.5 font-medium text-slate-600 dark:text-slate-300">
                              Nom du device
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanResult.deviceNames.map((name, idx) => (
                            <tr
                              key={`${name}-${idx}`}
                              className="border-t border-slate-200 dark:border-[#1E293B] hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                            >
                              <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{idx + 1}</td>
                              <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">{name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-[#1E293B]">
                    {orphanResult.deviceCount} device(s) unique(s)
                  </div>
                </div>
              )}

              {checkResult && (
                <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116] px-4 py-3 text-sm space-y-2">
                  <div className="font-medium text-slate-900 dark:text-white">Vérification</div>
                  <div className="text-slate-600 dark:text-slate-300">
                    total={checkResult.summary.total}, ok={checkResult.summary.ok}, missing={checkResult.summary.missing}, mismatched=
                    {checkResult.summary.mismatched}, invalid={checkResult.summary.invalid}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Si “missing” &gt; 0: l’attribute n’existe pas dans `smalt_core.attribute`. Si “mismatched” &gt; 0: un ou plusieurs champs ne
                    correspondent pas à la plateforme (mapping/topologie/ids).
                  </div>
                  <pre className="mt-2 p-3 rounded-md bg-[#0B1116] text-slate-100 overflow-x-auto text-xs leading-5 border border-slate-700">
                    {terminalLines.join('\n')}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

